import { Button, Checkbox, Input, Radio } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditOutlined } from "@ant-design/icons";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { QuizButtonClickEventName } from "event-tracker/button-click-event-name";
import { fetchYoutubeArtists } from "shared/service/StrmService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import YoutubeMockup from "shared/assets/images/youtube-mockup.png";

const defaultArtistImage =
  "https://placehold.co/100x100/292929/f2f2f2?text=Artist";

enum Step {
  Search = "SEARCH",
  Select = "SELECT",
  Help = "HELP",
}

type Result = {
  name?: string;
  id?: string;
  avatarUrl?: string;
};

type YoutubeSearchProps = {
  value?: Result;
  onChange?: (option?: Result) => void;
  onClickNext?: () => void;
};

const YoutubeSearch: React.FC<YoutubeSearchProps> = (props) => {
  const { value, onChange = (_: any) => {}, onClickNext } = props;
  const { t } = useTranslation();

  const [fieldValue, setFieldValue] = useState<string>("");
  // const [offset, setOffset] = useState<number>(0);
  // console.log(setOffset);
  const [searchField, setSearchField] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<Step>(Step.Search);
  const [results, setResults] = useState<Result[]>([]);
  const prevSearchFieldRef = useRef<string>("");

  const getYoutubeArtists = useCallback(async () => {
    if (searchField && searchField !== prevSearchFieldRef.current) {
      prevSearchFieldRef.current = searchField;

      dataLayerClickedEvent(QuizButtonClickEventName.SEARCH_SPOTIFY_PROFILE, {
        ...tagManagerEvent.Quiz.S4A_QUIZ_PLATFORMS_CONNECT,
      });
      setLoading(true);
      try {
        const artists: any = await fetchYoutubeArtists(
          encodeURIComponent(searchField)
        );
        const artistsOption = artists.map((artist: any) => {
          return {
            id: artist.snippet?.channelId,
            name: artist.snippet?.title,
            avatarUrl: artist.snippet?.thumbnails?.medium?.url,
          };
        });
        if (artistsOption.length === 0) {
          onChange({
            id: "no-data",
            name: t("quiz.pages.Questionario.youtubeNotFound"),
          });
        } else {
          if (artistsOption.length === 1) {
            onChange(artistsOption[0]);
            setStep(Step.Search);
          } else {
            setResults(artistsOption);
            setStep(Step.Select);
          }
        }
      } catch {
        onChange({
          id: "no-data",
          name: t("quiz.pages.Questionario.youtubeNotFound"),
        });
      } finally {
        setLoading(false);
      }
    }
  }, [searchField, onChange, t]);

  const onClickContinue = () => {
    if (onClickNext) onClickNext();
  };

  const onSelectArtist = (option?: Result) => {
    onChange(option);
  };

  const onSearch = () => {
    if (!fieldValue.trim()) return;
    if (searchField === fieldValue) {
      setStep(Step.Select);
    } else {
      setSearchField(fieldValue);
    }
  };

  const onClickEdit = () => {
    setStep(Step.Search);
    onSelectArtist();
  };

  const onClickNoValue = (checked: boolean) => {
    if (checked) {
      const noDataValue = { id: "no-data" };
      onChange(noDataValue);
      // setSelectedOption(noDataValue);
      // onSelectCallback(false);
    } else {
      onChange();
      // setSelectedOption(undefined);
    }
  };

  const onKeyPress = (event: any) => {
    if (event.which === 13) {
      onSearch();
    }
  };

  useEffect(() => {
    getYoutubeArtists();
  }, [getYoutubeArtists]);

  useEffect(() => {
    if (step === Step.Search) {
      dataLayerViewedEvent({
        ...tagManagerEvent.FreeAssessment.YOUTUBE_SEARCH_NEW_ASSESSMENT,
        version: "V3",
      });
    }
    if (step === Step.Select) {
      dataLayerViewedEvent({
        ...tagManagerEvent.FreeAssessment.YOUTUBE_SELECTION_NEW_ASSESSMENT,
        version: "V3",
      });
      return;
    }
    if (step === Step.Help) {
      dataLayerViewedEvent({
        ...tagManagerEvent.FreeAssessment.YOUTUBE_LINK_NEW_ASSESSMENT,
        version: "V3",
      });
      return;
    }
  }, [step]);

  const Footer = () => {
    return (
      <div className="footer">
        <Button
          className="botao-roxo"
          onClick={onClickContinue}
          disabled={!value}
        >
          {t("quiz.pages.FreeAssessment.continue")}
        </Button>
      </div>
    );
  };

  if (step === Step.Help) {
    return (
      <>
        <div className="full-item">
          <div className="caixa-input">
            <div className="field-tip">
              <h3>{t("quiz.pages.FreeAssessment.linkTip")}</h3>
              <div className="info">
                <div className="help-box">
                  <p>{t("quiz.pages.FreeAssessment.youtubeTipLink")}</p>
                </div>

                <div className="spotify-card">
                  <img src={YoutubeMockup} alt="Red Hot Chili Peppers" />
                </div>
              </div>
            </div>
            <h1 style={{ marginTop: 80 }}>
              {t("quiz.pages.FreeAssessment.useLink")}
            </h1>
            <Input
              placeholder={t("quiz.pages.Questionario.searchPlaceholder")}
              onChange={(event) => setFieldValue(event.target.value)}
              onKeyPress={onKeyPress}
              style={{ marginTop: 14 }}
              value={fieldValue}
              suffix={
                <Button
                  className="botao-roxo"
                  style={{ padding: 0, borderRadius: 2 }}
                  onClick={onSearch}
                  htmlType="button"
                  loading={loading}
                >
                  {t("quiz.pages.Questionario.buscar")}
                </Button>
              }
            />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (step === Step.Select)
    return (
      <>
        <h1>
          {t("quiz.pages.FreeAssessment.results", {
            artistsCount: results.length,
          })}
        </h1>
        <div className="search-value">
          <span>{searchField}</span>
          <EditOutlined onClick={onClickEdit} />
        </div>
        <div className="full-item results">
          {results.map((result) => (
            <div
              key={result.id}
              onClick={() => onSelectArtist(result)}
              className="result-item"
            >
              <img
                src={result.avatarUrl ?? defaultArtistImage}
                alt={result.name}
                className="image"
                onError={(e) => (e.currentTarget.src = defaultArtistImage)}
              />
              <span className="name">{result.name}</span>
              <Radio checked={result.id === value?.id}></Radio>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", marginTop: 20 }}>
            <span className="help-tip">
              {t("quiz.pages.FreeAssessment.didNotFoundProfile")}{" "}
              <b
                onClick={() => {
                  setFieldValue("");
                  setStep(Step.Help);
                }}
              >
                {t("quiz.pages.FreeAssessment.clickHere")}
              </b>
            </span>
          </div>
        </div>
        <Footer />
      </>
    );

  return (
    <>
      <FontAwesomeIcon icon={faYoutube} className="label-icon" />
      <h1> {t("quiz.pages.FreeAssessment.youtubeSearch")}</h1>
      {value?.avatarUrl && (
        <div className="profile-image">
          <img src={value?.avatarUrl} className="image" alt="artist" />
        </div>
      )}
      <span>{value?.name}</span>
      <div className="full-item">
        <div className="caixa-input">
          <Input
            placeholder={t("quiz.pages.Questionario.searchPlaceholder")}
            onChange={(event) => setFieldValue(event.target.value)}
            onKeyPress={onKeyPress}
            style={{ marginTop: 14 }}
            value={fieldValue}
            // prefix="/"
            suffix={
              <Button
                className="botao-roxo"
                style={{ padding: 0, borderRadius: 2 }}
                onClick={onSearch}
                htmlType="button"
                loading={loading}
              >
                {t("quiz.pages.Questionario.buscar")}
              </Button>
            }
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 6,
          }}
        >
          <Checkbox
            onChange={({ target: { checked } }) => onClickNoValue(checked)}
            checked={value?.id === "no-data"}
          >
            <span className="not-present">
              {t("quiz.pages.Questionario.noYoutube")}
            </span>
          </Checkbox>
        </div>
        <div
          style={{
            width: "100%",
            borderBottom: "1px solid #CECDFF",
            opacity: 0.2,
            margin: "12px 0",
          }}
        />
        <span className="help-tip">
          {t("quiz.pages.FreeAssessment.difficultyHint")},{" "}
          <b onClick={() => setStep(Step.Help)}>
            {t("quiz.pages.FreeAssessment.clickHere")}
          </b>
        </span>

        <div className="field-tip">
          <h3>{t("quiz.pages.FreeAssessment.havingProblem")}</h3>
          <div className="info">
            <div className="help-box">
              <p>{t("quiz.pages.FreeAssessment.youtubeTip")}</p>
            </div>

            <div className="spotify-card">
              <img src={YoutubeMockup} alt="Henrique & Juliano" />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default YoutubeSearch;
